import pluralize from 'pluralize'
import { func, array, object, string, bool } from 'prop-types'
import { Component, Fragment } from 'react'
import { Button, kuiperClassNames, LabeledSelect } from '@opallabs/kuiper'
import { AnalyticsLayer } from '@opallabs/quanta'
import { AccountMenuFooter } from './AccountMenuFooter'
import { EmptyAccountOptions } from './EmptyAccountOptions'
import {
  buildPlacementOptions,
  getDuration,
  calculateOpenPlacements,
  disassignPlacement,
  assignPlacement,
} from './helpers'

import './styles.scss'

const placementLabel = (openPlacementsPreviewer, scheduledAt) => (
  <Fragment>
    <p>Placement</p>
    {scheduledAt && (
      <AnalyticsLayer name="Placements Previewer Button">
        <Button
          type="invisible"
          icon="share"
          iconOptions={{
            width: '12px',
            height: '12px',
          }}
          label="View Placements"
          onClick={openPlacementsPreviewer}
          size="large"
        />
      </AnalyticsLayer>
    )}
  </Fragment>
)

const tooltipText =
  'Please contact your workspace administrator to add accounts'

const getNoOptionsElement = (
  openAccountCreator,
  accountTerm,
  canCreateAccount
) => (
  <EmptyAccountOptions
    openAccountCreator={openAccountCreator}
    contentText={`No ${pluralize.plural(
      accountTerm
    )} have been created for this channel yet`}
    accountTerm={accountTerm}
    tooltipText={tooltipText}
    canCreateAccount={canCreateAccount}
  />
)

const getCustomFooter = (openAccountCreator, accountTerm, canCreateAccount) => (
  <AccountMenuFooter
    openAccountCreator={openAccountCreator}
    accountTerm={accountTerm}
    tooltipText={tooltipText}
    canCreateAccount={canCreateAccount}
  />
)

class PostPlacementsControls extends Component {
  static propTypes = {
    accountId: string,
    accounts: array,
    accountTerm: string,
    canCreateAccount: bool,
    createPostPlacement: func,
    destroyPostPlacement: func,
    getPlacements: func,
    isOpeningPreviewer: bool,
    isSaved: bool,
    onAccountSelect: func,
    openAccountCreator: func,
    openPlacementsPreviewer: func,
    placements: array,
    post: object,
    postId: string,
    reset: bool,
    scheduledAt: string,
    selectedAccount: object,
    takedownScheduledAt: string,
    triggerDirtyState: func,
    triggerSaveFinished: func,
    updatePostPlacement: func,
    validAccount: bool,
  }

  static defaultProps = {
    isOpeningPreviewer: false,
    isSaved: false,
    placements: [],
    scheduledAt: null,
    takedownScheduledAt: null,
  }

  constructor(props) {
    super(props)
    this.state = {
      dirtyState: null,
      valueSet: false,
      isSaved: this.props.isSaved,
      scheduledAt: this.props.scheduledAt,
      takedownScheduledAt: this.props.takedownScheduledAt,
    }

    if (this.props.accountId !== null)
      this.props.getPlacements({ accountId: this.props.accountId })
  }

  componentDidUpdate = (prevProps, prevState) => {
    const {
      accountId,
      isOpeningPreviewer,
      placements,
      reset,
      scheduledAt,
      takedownScheduledAt,
    } = this.props
    const { isSaved, mappedOpenPlacements } = this.state

    // If Account changes update state
    if (prevProps.accountId !== accountId) {
      return this.resetPlacementsAndState()
    }

    // check if there is changes to be saved before opening Placements Previewer
    if (
      isOpeningPreviewer !== prevProps.isOpeningPreviewer &&
      isOpeningPreviewer &&
      !reset
    ) {
      return this.saveBeforeOpeningPreviewer()
    }

    // reset selectedValue if changes have been discarded
    if (reset !== prevProps.reset && reset && isOpeningPreviewer) {
      return this.resetBeforeOpeningPreviewer()
    }

    // if placements exist set openPlacement Amount
    if (prevProps.placements !== placements && placements.length > 0) {
      return this.setMappedPlacements()
    }

    // set openPlacementsAmount if mappedOpenPlacements have changed
    if (prevState.mappedOpenPlacements !== mappedOpenPlacements && !isSaved) {
      return this.setState({ openPlacementsAmount: this.setOpenPlacements() })
    }

    // set post scheduled dates if they change
    if (
      prevProps.scheduledAt !== scheduledAt ||
      prevProps.takedownScheduledAt !== takedownScheduledAt
    ) {
      return this.setState({
        scheduledAt,
        takedownScheduledAt,
        mappedOpenPlacements: calculateOpenPlacements(this.props),
      })
    }
  }

  componentWillUnmount() {
    this.setState({ selectedValue: {} })
  }

  resetPlacementsAndState = () => {
    const { accountId, scheduledAt, takedownScheduledAt, isSaved } = this.props
    if (accountId !== null) this.props.getPlacements({ accountId })

    this.setState({
      dirtyState: null,
      valueSet: false,
      isSaved,
      scheduledAt,
      takedownScheduledAt,
    })
  }

  saveBeforeOpeningPreviewer = () => {
    if (this.state.dirtyState !== null) return this.savePostPlacementUpdates()
    return this.props.triggerSaveFinished()
  }

  resetBeforeOpeningPreviewer = () => {
    const prevValue = this.setValue()
    this.setState({ selectedValue: prevValue })
    this.props.triggerDirtyState(prevValue)
    this.props.triggerSaveFinished()
  }

  setMappedPlacements = () => {
    const { scheduledAt, takedownScheduledAt, isSaved } = this.props
    const mappedOpenPlacements = calculateOpenPlacements(this.props)
    let state = {
      isSaved,
      scheduledAt,
      takedownScheduledAt,
      mappedOpenPlacements,
    }

    if (!this.state.valueSet) {
      state = { ...state, selectedValue: this.setValue(), valueSet: true }
    }

    this.setState(state, () => this.saveDirtyStateChanges())
  }

  saveDirtyStateChanges = () => {
    const { accountId, post } = this.props

    if (this.state.isSaved && this.state.dirtyState !== null) {
      this.savePostPlacementUpdates()
    }

    // if account is deselected and saved
    // and a placement is still saved on the post, delete it
    if (this.state.isSaved && accountId === null && post.placement) {
      this.props.destroyPostPlacement({
        content_id: post.id,
        placement_id: post.placement.id,
      })
    }
  }

  setValue = () => {
    const { placements, postId } = this.props
    let selectedValue = {}

    placements.forEach((placement) => {
      const placementPosts = placement.relationships.posts
      if (placementPosts && placementPosts.length) {
        placementPosts.forEach((post) => {
          if (post.id === postId) {
            selectedValue = {
              id: placement.id,
              title: placement.name,
              removable: true,
            }
          }
        })
      }
    })

    return selectedValue
  }

  setOpenPlacements = (newValue = null, action) => {
    let { mappedOpenPlacements } = this.state
    if (!mappedOpenPlacements.length) return 0

    const prevValue = this.state.selectedValue
    const savedOpenPlacements = calculateOpenPlacements(this.props)
    let currentlyOpen = mappedOpenPlacements.filter(
      (placement) => placement.isOpen
    ).length

    if (newValue !== null) {
      if (action === 'disassign') {
        mappedOpenPlacements = disassignPlacement(
          savedOpenPlacements,
          this.props
        )
      }
      if (action === 'assign') {
        mappedOpenPlacements = assignPlacement(
          mappedOpenPlacements,
          savedOpenPlacements,
          newValue,
          prevValue,
          this.props
        )
      }

      currentlyOpen = mappedOpenPlacements.filter(
        (placement) => placement.isOpen
      ).length
    }

    this.setState({ mappedOpenPlacements })
    return currentlyOpen
  }

  onPlacementSelect = (value) => {
    const newState =
      value.id === this.state.selectedValue.id
        ? this.disassignPostPlacement(value)
        : this.assignPostPlacement(value)

    this.props.triggerDirtyState(newState.selectedValue)
    this.setState({ ...newState })
  }

  assignPostPlacement = (selectedValue) => {
    const { post, postId } = this.props
    const dirtyState = {
      type: 'create',
      params: {
        content_id: postId,
        placement_id: selectedValue.id,
      },
    }
    // if post already has a placement on it,
    // set it to the update action instead
    if (post && post.placement) {
      dirtyState.type = 'update'
      dirtyState.params.prev_placement_id = post.placement.id
    }

    return {
      selectedValue,
      openPlacementsAmount: this.setOpenPlacements(selectedValue, 'assign'),
      dirtyState,
    }
  }

  disassignPostPlacement = (selectedValue) => ({
    selectedValue: {},
    openPlacementsAmount: this.setOpenPlacements(selectedValue, 'disassign'),
    dirtyState: {
      type: 'destroy',
      params: {
        content_id: this.props.postId,
        placement_id: selectedValue.id,
      },
    },
  })

  savePostPlacementUpdates = () => {
    const {
      dirtyState: { type, params },
    } = this.state

    // update Post Placement
    if (type === 'update') {
      this.props
        .updatePostPlacement(params)
        .then(() => this.props.triggerSaveFinished())
        .catch((err) => console.error(err))
    }
    // create Post Placement
    if (type === 'create') {
      this.props
        .createPostPlacement(params)
        .then(() => this.props.triggerSaveFinished())
        .catch((err) => console.error(err))
    }
    // destroy Post Placement
    if (type === 'destroy') {
      this.props
        .destroyPostPlacement(params)
        .then(() => this.props.triggerSaveFinished())
        .catch((err) => console.error(err))
    }

    this.setState({ dirtyState: null })
  }

  render() {
    const {
      placements,
      canCreateAccount,
      accountId,
      accountTerm = 'account',
      openAccountCreator,
      openPlacementsPreviewer,
      accounts,
      selectedAccount,
      onAccountSelect,
      validAccount,
    } = this.props
    const {
      scheduledAt,
      selectedValue,
      takedownScheduledAt,
      openPlacementsAmount,
    } = this.state
    const hasAccount = accountId !== null
    const hasPlacements = hasAccount && placements.length > 0
    const value = hasAccount ? selectedValue : {}

    return (
      <AnalyticsLayer name="Placement Controls">
        <div className={kuiperClassNames('post-placements-controls')}>
          <div
            className={kuiperClassNames('post-placements-control--assignment')}
          >
            <AnalyticsLayer name="Account Select">
              <LabeledSelect
                className="channel-account-select"
                customFooter={
                  accounts.length
                    ? getCustomFooter(
                        openAccountCreator,
                        accountTerm,
                        canCreateAccount
                      )
                    : undefined
                }
                labelText={<p>{accountTerm}</p>}
                onSelect={onAccountSelect}
                noOptionsElement={getNoOptionsElement(
                  openAccountCreator,
                  accountTerm,
                  canCreateAccount
                )}
                options={accounts}
                placeholder={`Choose ${accountTerm}`}
                secondaryAction={true}
                showDeselectIcon={true}
                value={selectedAccount}
                valid={validAccount}
              />
            </AnalyticsLayer>
            {hasPlacements && (
              <AnalyticsLayer name="Placement Select">
                <LabeledSelect
                  className="channel-account-select"
                  labelText={placementLabel(
                    openPlacementsPreviewer,
                    scheduledAt
                  )}
                  onSelect={this.onPlacementSelect}
                  options={buildPlacementOptions(placements)}
                  placeholder="Choose Placement"
                  secondaryAction={true}
                  showDeselectIcon={true}
                  shouldValidate={false}
                  value={value}
                />
              </AnalyticsLayer>
            )}
          </div>
          {hasPlacements && scheduledAt && (
            <div
              className={kuiperClassNames('post-placements-control--preview')}
            >
              <p>
                This {accountTerm.toLowerCase()} has{' '}
                <span>
                  {openPlacementsAmount < 0 ? '0' : openPlacementsAmount} open{' '}
                  {openPlacementsAmount === 1 ? 'placement' : 'placements'}
                </span>{' '}
                {getDuration(scheduledAt, takedownScheduledAt)}
              </p>
            </div>
          )}
        </div>
      </AnalyticsLayer>
    )
  }
}
PostPlacementsControls.displayName = 'Photon: PostPlacementsControls'

export default PostPlacementsControls
